import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { PlusIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useAuth, ROLE_ADMIN } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

interface Technology {
    id: string;
    name: string;
    description: string;
    category: string;
    resourceTypeId: string;
}

interface ResourceType {
    id: string;
    name: string;
    description: string;
    technologies?: Technology[];
}

export default function ResourceTypes() {
    const { user, userHasRole } = useAuth();
    const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isResourceTypeModalOpen, setIsResourceTypeModalOpen] = useState(false);
    const [isTechnologyModalOpen, setIsTechnologyModalOpen] = useState(false);
    const [editingType, setEditingType] = useState<ResourceType | null>(null);
    const [editingTech, setEditingTech] = useState<Technology | null>(null);
    const [selectedResourceType, setSelectedResourceType] = useState<string | null>(null);
    const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });
    const [techFormData, setTechFormData] = useState({
        name: '',
        description: '',
        category: ''
    });

    const canViewAdminPage = useMemo(() => {
        if (!user) return false;
        return userHasRole(ROLE_ADMIN);
    }, [user, userHasRole]);

    const loadResourceTypes = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'resourceTypes'));
            const types = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as ResourceType[];

            // Carregar tecnologias para cada tipo de recurso
            const typesWithTechs = await Promise.all(types.map(async (type) => {
                const techsQuery = query(collection(db, 'technologies'), where('resourceTypeId', '==', type.id));
                const techsSnapshot = await getDocs(techsQuery);
                const technologies = techsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as Technology[];
                return { ...type, technologies };
            }));

            setResourceTypes(typesWithTechs);
        } catch (err) {
            console.error('Erro ao carregar tipos de recurso:', err);
            setError('Erro ao carregar tipos de recurso');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (canViewAdminPage) {
            loadResourceTypes();
        }
    }, [canViewAdminPage]);

    if (!user) {
        return <div className="text-gray-500">Carregando usuário...</div>;
    }

    if (!canViewAdminPage) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold text-gray-700 mb-4">Acesso Negado</h1>
                    <p className="text-gray-500">Você não tem permissão para visualizar esta página.</p>
                    <Link to="/" className="mt-6 inline-block bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700">
                        Voltar para o Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (loading) {
        return <div className="text-gray-500">Carregando tipos de recurso...</div>;
    }

    const handleResourceTypeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        try {
            if (editingType) {
                await updateDoc(doc(db, 'resourceTypes', editingType.id), formData);
            } else {
                await addDoc(collection(db, 'resourceTypes'), formData);
            }
            await loadResourceTypes();
            handleCloseResourceTypeModal();
        } catch (err) {
            console.error('Erro ao salvar tipo de recurso:', err);
            setError('Erro ao salvar tipo de recurso');
        }
    };

    const handleTechnologySubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!selectedResourceType) return;

        try {
            const techData = {
                ...techFormData,
                resourceTypeId: selectedResourceType
            };

            if (editingTech) {
                await updateDoc(doc(db, 'technologies', editingTech.id), techData);
            } else {
                await addDoc(collection(db, 'technologies'), techData);
            }
            await loadResourceTypes();
            handleCloseTechnologyModal();
        } catch (err) {
            console.error('Erro ao salvar tecnologia:', err);
            setError('Erro ao salvar tecnologia');
        }
    };

    const handleEditResourceType = (type: ResourceType) => {
        setEditingType(type);
        setFormData({
            name: type.name,
            description: type.description
        });
        setIsResourceTypeModalOpen(true);
    };

    const handleEditTechnology = (tech: Technology) => {
        setEditingTech(tech);
        setTechFormData({
            name: tech.name,
            description: tech.description,
            category: tech.category
        });
        setSelectedResourceType(tech.resourceTypeId);
        setIsTechnologyModalOpen(true);
    };

    const handleDeleteResourceType = async (id: string) => {
        if (window.confirm('Tem certeza que deseja excluir este tipo de recurso? Todas as tecnologias associadas também serão excluídas.')) {
            try {
                // Primeiro, excluir todas as tecnologias associadas
                const techsQuery = query(collection(db, 'technologies'), where('resourceTypeId', '==', id));
                const techsSnapshot = await getDocs(techsQuery);
                const deletePromises = techsSnapshot.docs.map(doc => deleteDoc(doc.ref));
                await Promise.all(deletePromises);

                // Depois, excluir o tipo de recurso
                await deleteDoc(doc(db, 'resourceTypes', id));
                await loadResourceTypes();
            } catch (err) {
                console.error('Erro ao excluir tipo de recurso:', err);
                setError('Erro ao excluir tipo de recurso');
            }
        }
    };

    const handleDeleteTechnology = async (id: string) => {
        if (window.confirm('Tem certeza que deseja excluir esta tecnologia?')) {
            try {
                await deleteDoc(doc(db, 'technologies', id));
                await loadResourceTypes();
            } catch (err) {
                console.error('Erro ao excluir tecnologia:', err);
                setError('Erro ao excluir tecnologia');
            }
        }
    };

    const handleCloseResourceTypeModal = () => {
        setIsResourceTypeModalOpen(false);
        setEditingType(null);
        setFormData({ name: '', description: '' });
    };

    const handleCloseTechnologyModal = () => {
        setIsTechnologyModalOpen(false);
        setEditingTech(null);
        setTechFormData({ name: '', description: '', category: '' });
        setSelectedResourceType(null);
    };

    const toggleExpand = (typeId: string) => {
        setExpandedTypes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(typeId)) {
                newSet.delete(typeId);
            } else {
                newSet.add(typeId);
            }
            return newSet;
        });
    };

    return (
        <div className="space-y-6">
            <div className="sm:flex sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        Tipos de Recurso
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Gerencie os tipos de recursos e suas tecnologias.
                    </p>
                </div>
                <div className="mt-4 sm:mt-0">
                    <button
                        type="button"
                        onClick={() => setIsResourceTypeModalOpen(true)}
                        className="btn-primary"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        Novo Tipo
                    </button>
                </div>
            </div>

            {error && (
                <div className="rounded-md bg-red-50 p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">{error}</h3>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <div className="flow-root">
                        <ul role="list" className="-my-5 divide-y divide-gray-200">
                            {resourceTypes.map((type) => (
                                <li key={type.id} className="py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center">
                                                <button
                                                    onClick={() => toggleExpand(type.id)}
                                                    className="mr-2 text-gray-400 hover:text-gray-500"
                                                >
                                                    {expandedTypes.has(type.id) ? (
                                                        <ChevronUpIcon className="h-5 w-5" />
                                                    ) : (
                                                        <ChevronDownIcon className="h-5 w-5" />
                                                    )}
                                                </button>
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {type.name}
                                                </p>
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                {type.description}
                                            </p>
                                        </div>
                                        <div className="flex-shrink-0 flex space-x-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedResourceType(type.id);
                                                    setIsTechnologyModalOpen(true);
                                                }}
                                                className="btn-secondary"
                                            >
                                                <PlusIcon className="h-5 w-5 mr-2" />
                                                Nova Tecnologia
                                            </button>
                                            <button
                                                onClick={() => handleEditResourceType(type)}
                                                className="p-1 rounded-full text-gray-400 hover:text-gray-500"
                                            >
                                                <PencilIcon className="h-5 w-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteResourceType(type.id)}
                                                className="p-1 rounded-full text-gray-400 hover:text-red-500"
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                    {expandedTypes.has(type.id) && type.technologies && (
                                        <div className="mt-4 ml-6">
                                            <h4 className="text-sm font-medium text-gray-900 mb-2">Tecnologias</h4>
                                            <ul className="space-y-2">
                                                {type.technologies.map((tech) => (
                                                    <li key={tech.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{tech.name}</p>
                                                            <p className="text-xs text-gray-500">{tech.description}</p>
                                                            <p className="text-xs text-gray-400">Categoria: {tech.category}</p>
                                                        </div>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => handleEditTechnology(tech)}
                                                                className="p-1 rounded-full text-gray-400 hover:text-gray-500"
                                                            >
                                                                <PencilIcon className="h-4 w-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteTechnology(tech.id)}
                                                                className="p-1 rounded-full text-gray-400 hover:text-red-500"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Modal de Tipo de Recurso */}
            {isResourceTypeModalOpen && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {editingType ? 'Editar Tipo de Recurso' : 'Novo Tipo de Recurso'}
                        </h3>
                        <form onSubmit={handleResourceTypeSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                    Nome
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="input-primary mt-1"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                    Descrição
                                </label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="input-primary mt-1"
                                    rows={3}
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={handleCloseResourceTypeModal}
                                    className="btn-secondary"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                >
                                    {editingType ? 'Atualizar' : 'Salvar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de Tecnologia */}
            {isTechnologyModalOpen && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            {editingTech ? 'Editar Tecnologia' : 'Nova Tecnologia'}
                        </h3>
                        <form onSubmit={handleTechnologySubmit} className="space-y-4">
                            <div>
                                <label htmlFor="techName" className="block text-sm font-medium text-gray-700">
                                    Nome
                                </label>
                                <input
                                    type="text"
                                    id="techName"
                                    value={techFormData.name}
                                    onChange={(e) => setTechFormData({ ...techFormData, name: e.target.value })}
                                    className="input-primary mt-1"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="techCategory" className="block text-sm font-medium text-gray-700">
                                    Categoria
                                </label>
                                <input
                                    type="text"
                                    id="techCategory"
                                    value={techFormData.category}
                                    onChange={(e) => setTechFormData({ ...techFormData, category: e.target.value })}
                                    className="input-primary mt-1"
                                    required
                                    placeholder="Ex: Frontend, Backend, Mobile, etc."
                                />
                            </div>
                            <div>
                                <label htmlFor="techDescription" className="block text-sm font-medium text-gray-700">
                                    Descrição
                                </label>
                                <textarea
                                    id="techDescription"
                                    value={techFormData.description}
                                    onChange={(e) => setTechFormData({ ...techFormData, description: e.target.value })}
                                    className="input-primary mt-1"
                                    rows={3}
                                />
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={handleCloseTechnologyModal}
                                    className="btn-secondary"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary"
                                >
                                    {editingTech ? 'Atualizar' : 'Salvar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
} 